<template>
  <div class="caa-root">

    <!-- Account selector + quick actions (compact version of the client
         modal's account tab header — same modals, same store actions) -->
    <div v-if="hasAccounts" class="caa-strip">
      <button id="caa-account-button" class="caa-selector" :class="{ demo: selectedAccount.isDemo }">
        <span class="caa-selector__icon"><i class="fa-solid fa-wallet"></i></span>
        <span class="caa-selector__info">
          <span class="caa-selector__label">{{ $t('columns.account') }}</span>
          <span class="caa-selector__value">
            #{{ selectedAccount.id }}
            <span v-if="selectedAccount.isDemo" class="caa-pill caa-pill--demo">DEMO</span>
            <span v-else class="caa-pill caa-pill--real">REAL</span>
          </span>
        </span>
        <span class="caa-selector__balance">
          {{ (selectedAccount.balance || 0) | numberFormatter }} {{ selectedAccount.currency }}
        </span>
        <i class="fa-solid fa-chevron-down caa-selector__chevron"></i>
      </button>

      <div class="caa-actions">
        <button class="caa-action" :class="selectedAccount.active ? 'on-success' : 'on-danger'"
                @click="accountActiveToggle">
          <i class="fa-solid" :class="{'fa-circle-check': selectedAccount.active, 'fa-circle-xmark': !selectedAccount.active}"></i>
          <span>{{ selectedAccount.active ? $t('columns.active') : $t('columns.inactive') }}</span>
        </button>
        <button v-if="hasPermission(permissions.ACCOUNT_DELETE)" class="caa-action caa-action--delete"
                :title="$t('account.delete.action')" @click="$refs.deleteAccountModal.show()">
          <i class="fa-solid fa-trash-can"></i>
          <span>{{ $t('account.delete.action') }}</span>
        </button>
        <button class="caa-action" @click="$refs.accountSyncModal.showModal()">
          <i class="fa-solid fa-rotate"></i>
          <span>{{ $t('columns.sync') }}</span>
        </button>
        <button class="caa-action" @click="$refs.tradeReportModal.show()">
          <i class="fa-solid fa-file-invoice"></i>
          <span>{{ $t('account.report.action') }}</span>
        </button>
        <button class="caa-action caa-action--new" @click="newAccount">
          <i class="fa-solid fa-plus"></i>
          <span>{{ $t('account.newAccount') }}</span>
        </button>
      </div>
    </div>

    <div v-else-if="!loading" class="caa-empty">
      <span class="caa-empty__text"><i class="fa-solid fa-wallet mr-50"></i>{{ $t('account.noAccounts') }}</span>
      <b-button size="sm" variant="primary" @click="newAccount">
        <i class="fa-solid fa-plus mr-50"></i>{{ $t('account.newAccount') }}
      </b-button>
    </div>

    <b-popover v-if="hasAccounts" ref="popover" custom-class="account-list-popover" placement="bottom" target="caa-account-button">
      <b-list-group class="m-0 p-0">
        <b-list-group-item class="cursor-pointer" v-for="acc in accounts" v-bind:key="acc.id" @click="onAccountSelect(acc.id)">
          #{{acc.id}} <span class="ca-acct-pill" :class="acc.isDemo ? 'ca-acct-pill-demo' : 'ca-acct-pill-real'">{{ acc.isDemo ? 'DEMO' : 'REAL' }}</span>
        </b-list-group-item>
        <b-list-group-item class="cursor-pointer ca-acct-new-row" @click="newAccount">
          <i class="fa-sharp fa-solid fa-plus mr-1"></i>{{$t('account.newAccount')}}
        </b-list-group-item>
      </b-list-group>
    </b-popover>

    <new-account-modal ref="account-modal" @created="onAccountCreated"/>
    <account-sync-modal ref="accountSyncModal" v-bind:account-id="selectedAccountId" v-bind:account-currency="selectedAccountCurrency"/>
    <account-trade-report-modal ref="tradeReportModal" v-bind:account-id="selectedAccountId"
                                v-bind:currency="selectedAccountCurrency" v-bind:client="client"/>

    <b-modal ref="deleteAccountModal" centered hide-header hide-footer modal-class="ca-del-modal">
      <div class="ca-del-header">
        <div class="ca-del-icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
        <div class="ca-del-title">{{ $t('account.delete.title', { id: selectedAccount.id }) }}</div>
        <div class="ca-del-text">{{ $t('account.delete.description') }}</div>
      </div>

      <b-alert :show="!canDeleteSelectedAccount" variant="danger" class="ca-del-guard">
        <div class="ca-del-guard-title">
          <i class="fa-solid fa-ban mr-1"></i>{{ $t('account.delete.blockedTitle') }}
        </div>
        <ul class="ca-del-guard-list">
          <li v-if="selectedAccount.totalOpenTrades > 0">
            {{ $t('account.delete.blockedOpenTrades', { count: selectedAccount.totalOpenTrades }) }}
          </li>
          <li v-if="(selectedAccount.balance || 0) !== 0">
            {{ $t('account.delete.blockedBalance', { balance: selectedAccount.balance }) }}
          </li>
          <li v-if="(selectedAccount.credit || 0) !== 0">
            {{ $t('account.delete.blockedCredit', { credit: selectedAccount.credit }) }}
          </li>
        </ul>
      </b-alert>

      <div class="ca-del-actions">
        <b-button variant="outline-secondary" :disabled="deletingAccount" @click="$refs.deleteAccountModal.hide()">
          {{ $t('operations.cancel') }}
        </b-button>
        <b-button variant="danger" :disabled="!canDeleteSelectedAccount || deletingAccount" @click="confirmDeleteAccount">
          <b-spinner v-if="deletingAccount" small class="mr-1"/>
          <i v-else class="fa-solid fa-trash-can mr-1"></i>{{ $t('account.delete.confirm') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import NewAccountModal from '@/components/modal/newAccountModal.vue'
import AccountSyncModal from '@/components/modal/AccountSyncModal.vue'
import AccountTradeReportModal from '@/components/modal/AccountTradeReportModal.vue'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPopover from '@/components/mixin/mixinPopover'
import mixinPermissions from '@/components/mixin/mixinPermissions'

export default {
  name: 'ClientAccountActions',
  mixins: [mixinBase, mixinNotifications, mixinPopover, mixinPermissions],
  components: { NewAccountModal, AccountSyncModal, AccountTradeReportModal },
  props: {
    clientId: String,
    organizationId: String,
    client: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    loading: true,
    accounts: [],
    deletingAccount: false,
    selectedAccount: { id: '0', active: false }
  }),
  watch: {
    clientId: {
      handler() {
        this.refresh()
      },
      immediate: true
    }
  },
  computed: {
    hasAccounts() {
      return Array.isArray(this.accounts) && this.accounts.length > 0;
    },
    selectedAccountId() {
      return this.selectedAccount?.id || null;
    },
    selectedAccountCurrency() {
      return this.selectedAccount?.currency || 'EUR';
    },
    canDeleteSelectedAccount() {
      return this.selectedAccount != null
          && !(this.selectedAccount.totalOpenTrades > 0)
          && (this.selectedAccount.balance || 0) === 0
          && (this.selectedAccount.credit || 0) === 0;
    }
  },
  methods: {
    ...mapActions('account', ['getAccountsByClient', 'setActive', 'deleteAccount']),
    async refresh() {
      try {
        this.loading = true;
        this.accounts = await this.getAccountsByClient(this.clientId);
        this.setDefaultSelectedAccount()
      } catch (e) {
        console.error('refresh accounts error', e)
      } finally {
        this.loading = false;
      }
    },
    setDefaultSelectedAccount() {
      if (this.selectedAccount != null && this.selectedAccount.id !== '0') {
        let acc = this.accounts.find(x => x.id === this.selectedAccount.id);
        if (acc != null) {
          this.selectedAccount = { ...acc };
          return;
        }
      }
      if (Array.isArray(this.accounts) && this.accounts.length > 0) {
        this.selectedAccount = { ...this.accounts[0] };
      } else {
        this.selectedAccount = { id: '0', active: false }
      }
    },
    onAccountSelect(id) {
      try {
        if (this.selectedAccount.id === id) {
          return
        }
        this.selectedAccount = this.accounts.find(x => x.id === id)
      } finally {
        this.closeAllPopovers();
      }
    },
    newAccount() {
      this.$refs['account-modal'].showModal(this.clientId, this.organizationId)
    },
    onAccountCreated(account) {
      if (!Array.isArray(this.accounts)) {
        this.accounts = []
      }
      this.accounts.push(account)
      this.selectedAccount = account
    },
    accountActiveToggle() {
      this.setActive({ account: this.selectedAccount.id, active: !this.selectedAccount.active })
          .then(this.refresh)
    },
    async confirmDeleteAccount() {
      if (!this.canDeleteSelectedAccount || this.deletingAccount) {
        return
      }
      const accountId = this.selectedAccount.id
      try {
        this.deletingAccount = true
        await this.deleteAccount(accountId)
        this.showSuccessNotification(this.$t('account.delete.successTitle'), this.$t('account.delete.successMessage', { id: accountId }))
        this.$refs.deleteAccountModal.hide()
        await this.refresh()
      } catch (e) {
        this.showErrorNotification(this.$t('account.delete.errorTitle'), this.getErrorDescription(e.response ? e.response.data : e), null, e)
      } finally {
        this.deletingAccount = false
      }
    },
  }
}
</script>

<style lang="scss">
/* Shared with ClientAccounts.vue — duplicated here so the activity page works
   without the client modal's chunk ever loading. */
.account-list-popover {
  .popover-body { padding: 0; }
  .list-group-item {
    border-radius: 0;
    border-left: none;
    border-right: none;
    font-weight: 500;
    transition: background 0.15s ease, color 0.15s ease;
    &:first-child { border-top: none; }
    &:hover { background: #eff6ff; color: #2563eb; }
  }
  .ca-acct-new-row {
    color: #2563eb;
    font-weight: 600;
    border-top: 2px solid #e2e8f0;
  }
  .ca-acct-pill {
    margin-left: 6px;
    font-size: 0.62rem;
    font-weight: 700;
    padding: 1px 7px;
    border-radius: 999px;
    letter-spacing: 0.5px;
  }
  .ca-acct-pill-demo { background: #fef3c7; color: #92400e; }
  .ca-acct-pill-real { background: #dcfce7; color: #166534; }
}

.ca-del-modal {
  .modal-body { padding: 24px; }
  .ca-del-header { text-align: center; margin-bottom: 16px; }
  .ca-del-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 12px;
    border-radius: 50%;
    background: #fef2f2;
    color: #dc2626;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.7rem;
  }
  .ca-del-title {
    font-size: 1.15rem;
    font-weight: 800;
    color: #dc2626;
    margin-bottom: 6px;
  }
  .ca-del-text { font-size: 0.87rem; color: #475569; }
  .ca-del-guard {
    font-size: 0.84rem;
    margin-bottom: 0;
  }
  .ca-del-guard-title { font-weight: 700; margin-bottom: 6px; }
  .ca-del-guard-list {
    margin: 0;
    padding-left: 18px;
    li { margin-bottom: 4px; }
  }
  .ca-del-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 18px;
  }
}
.dark-layout .ca-del-modal {
  .ca-del-icon { background: rgba(239, 68, 68, 0.15); color: #ff8a95; }
  .ca-del-title { color: #ff8a95; }
  .ca-del-text { color: #cbd5e1; }
}
</style>

<style scoped lang="scss">
.caa-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.caa-selector {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  text-align: left;

  &:hover {
    border-color: #7367f0;
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.14);
  }

  &__icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: linear-gradient(135deg, #9e95f5 0%, #7367f0 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    flex-shrink: 0;
    box-shadow: 0 2px 6px rgba(115, 103, 240, 0.3);
  }

  &.demo &__icon {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    box-shadow: 0 2px 6px rgba(245, 158, 11, 0.25);
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__label {
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #94a3b8;
  }

  &__value {
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__balance {
    margin-left: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(115, 103, 240, 0.08);
    color: #7367f0;
    font-size: 0.76rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__chevron {
    color: #94a3b8;
    font-size: 0.7rem;
  }
}

.caa-pill {
  font-size: 0.6rem;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 999px;
  letter-spacing: 0.6px;

  &--demo { background: #fef3c7; color: #92400e; }
  &--real { background: #dcfce7; color: #166534; }
}

.caa-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.caa-action {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #7367f0;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.08);
  }

  &.on-success {
    background: #f0fdf4;
    border-color: rgba(22, 163, 74, 0.3);
    color: #16a34a;

    &:hover {
      background: #16a34a;
      color: #fff;
      border-color: #16a34a;
    }
  }

  &.on-danger {
    background: #fef2f2;
    border-color: rgba(220, 38, 38, 0.3);
    color: #dc2626;

    &:hover {
      background: #dc2626;
      color: #fff;
      border-color: #dc2626;
    }
  }

  &--delete {
    color: #dc2626;
    border-color: rgba(220, 38, 38, 0.35);

    &:hover {
      background: #dc2626;
      border-color: #dc2626;
      color: #fff;
    }
  }

  &--new {
    background: #7367f0;
    border-color: #7367f0;
    color: #fff;

    &:hover {
      background: #7367f0;
      border-color: #7367f0;
      color: #fff;
      filter: brightness(1.08);
    }
  }
}

.caa-empty {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;

  &__text {
    font-size: 0.87rem;
    color: #94a3b8;
    font-weight: 500;
  }
}

body.dark-layout {
  .caa-selector {
    background: #2a2f3a;
    border-color: #475569;

    &__value { color: #f1f5f9; }
  }

  .caa-action {
    background: #2a2f3a;
    border-color: #475569;
    color: #f1f5f9;

    &.on-success { background: rgba(34, 197, 94, 0.18); color: #6dd29a; }
    &.on-danger { background: rgba(239, 68, 68, 0.18); color: #ff8a95; }

    &--delete { color: #ff8a95; border-color: rgba(239, 68, 68, 0.4); }
    &--new { background: #7367f0; border-color: #7367f0; color: #fff; }
  }

  .caa-pill--demo { background: rgba(251, 191, 36, 0.18); color: #fcd34d; }
  .caa-pill--real { background: rgba(34, 197, 94, 0.18); color: #6dd29a; }
}
</style>
